import {
    ActionIcon,
    Box,
    Button,
    Grid, Group,
    Loader,
    Menu,
    Paper,
    Progress,
    Stack,
    Text,
    Title, Tooltip, UnstyledButton,
    useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaChevronLeft, FaFacebook, FaLinkedin, FaPinterest, FaReddit, FaTimesCircle, FaTwitter, FaWhatsapp } from "react-icons/fa";
import { FaArrowLeft, FaImage, FaShare } from "react-icons/fa6";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import InputField, { InputFieldTypes } from "../components/input_field";
import Intercom from "../components/integrations/intercom";
import OpenAi from "../components/integrations/open_ai";
import Retell from "../components/integrations/retell";
import Sendgrid from "../components/integrations/sendgrid";
import Stripe from "../components/integrations/stripe";
import Twilio from "../components/integrations/twilio";
import { useApp } from "../contexts/app.context";
import { useAuth } from "../contexts/auth.context";
import { CONSULT_LABEL_AVAILABILITY, GET_COMPANY_ONBOARDING_DATA, SAVE_COMPANY_ONBOARDING_DATA } from "../services/companies";
import { countriesList, phoneCountryCodes } from "../utility/util";

export default function Onboarding({ }) {
    const { userData, role, companyHost, loadUserData } = useAuth();
    const { configs, setConfigs } = useApp();
    const theme = useMantineTheme();

    const [openImage, setOpenImage] = useState<boolean>(false);
    const [openIcon, setOpenIcon] = useState<boolean>(false);
    const [selectedIntegration, setSelectedIntegration] = useState<string>();

    const navigate = useNavigate();
    const { hash } = useLocation();

    const isMobile = useMediaQuery('(max-width: 900px)');

    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [steps, setSteps] = useState<any[]>([
        { tag: "user", title: "Getting Started", key: "general", subtitle: "Welcome! First things first..." },
        { tag: "brand", title: "Getting Started", key: "general", subtitle: "Welcome! First things first..." },
        { tag: "whitelabel", title: "Customize your Client Portal", key: "whitelabel", subtitle: "Welcome! First things first..." },
        { tag: "integrations", title: "Connect The Tools", key: "integrations", subtitle: "Welcome! First things first..." },
        { title: "Share With Your Clients", subtitle: "Welcome! First things first..." },
    ]);
    const [data, setData] = useState<any>({});
    const [consultingLabel, setConsultingLabel] = useState<boolean>(false);
    const [validLabel, setValidLabel] = useState<boolean>(false);

    const handleChangeUser = (dt: any) => setData((c: any) => ({ ...c, user: { ...c.user, ...dt } }))
    const handleChangeGeneral = (dt: any) => setData((c: any) => ({ ...c, general: { ...c.general, ...dt } }))
    const handleChangeWhitelabel = (dt: any) => setData((c: any) => ({ ...c, whitelabel: { ...c.whitelabel, ...dt } }))

    const navigateToTab = (index: number, save = false) => {
        const cb = () => {
            if (index === steps.length) {
                loadUserData();
                navigate("/");
            } else setSelectedTab(index);
        }
        const key = steps[index - 1]?.key;
        if (save && key) {
            setLoading(true)
            SAVE_COMPANY_ONBOARDING_DATA({ key, data })
                .then(() => {
                    setLoading(false);
                    cb();
                })
                .catch((err) => {
                    notifications.show({ message: err.message, color: "red" })
                    setLoading(false)
                })
        } else {
            cb();
        }
    }

    const UserStep = <Stack>
        <InputField
            name="name"
            title="Enter your name"
            value={data?.user?.name}
            onChange={handleChangeUser}
        />

        <InputField
            name="email"
            title="Enter your email"
            value={data?.user?.email}
            onChange={handleChangeUser}
        />

        {!data?.user?.password_set && <InputField
            name="password"
            title="Choose a password"
            value={data?.user?.password}
            fieldType={InputFieldTypes.PASSWORD}
            onChange={handleChangeUser}
        />}

        <Group style={isMobile ? { display: 'block' } : {}}>
            <InputField
                style={isMobile ? {} : { flex: 0.3 }}
                title={"Country"}
                name="phone_code"
                autoComplete="tel-country-code"
                fieldType={InputFieldTypes.SELECT}
                value={data?.user?.phone_code}
                onChange={handleChangeUser}
                nothingFound="No country code found"
                options={phoneCountryCodes}
            />
            <InputField
                style={isMobile ? {} : { flex: 0.7 }}
                title={"Phone"}
                name="phone"
                value={data?.user?.phone}
                onChange={handleChangeUser}
            />
        </Group>
    </Stack>;

    const CompanyStep = <Box>
        <InputField
            name="name"
            title="Enter your brand name"
            value={data?.general?.name}
            onChange={handleChangeGeneral}
        />

        <InputField
            name="country"
            title="Select Your Country"
            mt="md"
            fieldType={InputFieldTypes.SELECT}
            options={countriesList}
            value={data?.general?.country}
            onChange={handleChangeGeneral}
        />
    </Box>;

    const WhitelabelStep = <Stack>
        <Grid>
            <Grid.Col span={{ md: 6 }}>
                <Paper shadow="xs" p="md" style={{ background: theme.colors.dark[9] }}>
                    <Box>
                        {
                            configs.image
                                ? <img src={configs.image} height={80} width={200} color="gray" style={{ objectFit: 'contain' }} />
                                : <FaImage size={80} color={theme.colors.gray[2]} />
                        }
                    </Box>
                    <Menu opened={openImage} onOpen={() => setOpenImage(true)} onClose={() => setOpenImage(false)}>
                        <Menu.Target>
                            <UnstyledButton>
                                <Button variant="outline" color="gray">Insert your logo</Button>
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <InputField
                                name="image"
                                title='Logo'
                                value={null}
                                onChange={({ image }) => {
                                    setOpenIcon(false);
                                    handleChangeWhitelabel({ image })
                                    if (image) setConfigs({ image })
                                }}
                                fieldType={InputFieldTypes.IMAGE}
                            />
                        </Menu.Dropdown>
                    </Menu>
                </Paper>
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
                <Paper shadow="xs" p="md" style={{ background: theme.colors.dark[9] }}>
                    <Box>
                        {
                            configs.icon
                                ? <img src={configs.icon} height={80} width={200} color="gray" style={{ objectFit: 'contain' }} />
                                : <FaImage size={80} color={theme.colors.gray[2]} />
                        }
                    </Box>
                    <Menu opened={openIcon} onOpen={() => setOpenIcon(true)} onClose={() => setOpenIcon(false)}>
                        <Menu.Target>
                            <UnstyledButton>
                                <Button variant="outline" color="gray">Insert your icon</Button>
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <InputField
                                name="icon"
                                title='Icon'
                                value={null}
                                onChange={({ icon }) => {
                                    setOpenIcon(false);
                                    handleChangeWhitelabel({ icon })
                                    if (icon) setConfigs({ icon })
                                }}
                                fieldType={InputFieldTypes.IMAGE}
                            />
                        </Menu.Dropdown>
                    </Menu>
                </Paper>
            </Grid.Col>
        </Grid>

        <InputField
            name="primary"
            title="Primary Color"
            fieldType={InputFieldTypes.COLOR}
            value={data?.whitelabel?.primary}
            onChange={({ primary }) => {
                setOpenIcon(false);
                handleChangeWhitelabel({ primary })
                if (primary) setConfigs({ primary })
            }}
        />

        <InputField
            name="label"
            title="Company URL (Subdomain)"
            value={data?.whitelabel?.label}
            leftSection={
                consultingLabel
                    ? <Loader size="xs" />
                    : validLabel ? <FaCheckCircle color="green" /> : <FaTimesCircle color="red" />
            }
            rightSection={<Text style={{ marginLeft: -120 }}>.awaz.ai</Text>}
            onChange={({ label }) => {
                label = label.replace(/ |\.|\[|\]|\(|\)|\@|\!|\*|\,|\;|\:/g, "").toLowerCase();
                handleChangeWhitelabel({ label });

                if (label?.length >= 3) {
                    setConsultingLabel(true);
                    CONSULT_LABEL_AVAILABILITY(label)
                        .then(({ available }) => {
                            setValidLabel(available);
                            setConsultingLabel(false);
                        })
                        .catch(err => {
                            setValidLabel(false)
                            setConsultingLabel(false);
                        })
                } else {
                    setValidLabel(false);
                }
            }}
        />
    </Stack>;

    const IntegrationsStep = <Stack>
        {
            [
                { title: `OpenAi`, key: "OpenAi", url: "https://platform.openai.com/account/api-keys", required: true },
                { title: `Twilio`, key: "Twilio", url: "https://console.twilio.com/us1/account/keys-credentials/api-keys", required: true },
                { title: `Retell`, key: "Retell", url: "https://dashboard.retellai.com/account/api-keys", required: true },
                { title: `Stripe`, key: "Stripe", url: "https://dashboard.stripe.com/apikeys", required: false },
                { title: `Sendgrid`, key: "Sendgrid", url: "https://app.sendgrid.com/settings/api_keys", required: false },
                { title: `Intercom`, key: "Intercom", url: "https://app.intercom.com/a/apps/_/developer-hub", required: false },
            ].map((item: any) => (
                <UnstyledButton onClick={() => setSelectedIntegration(item.key)}>
                    <Paper p="md" style={{
                        border: selectedIntegration === item.key ? `3px solid ${theme.colors.gray[2]}` : '0px solid #0a0a0a',
                        background: theme.colors.dark[item.required ? 9 : 6],
                    }}>
                        <Group>
                            <Text c="gray" style={{ flex: 1 }}>{item.title}{item.required ? " (required)" : ""}</Text>
                            {item.url && <Tooltip label="Get Keys">
                                <ActionIcon variant="outline" color="gray" onClick={() => window.open(item.url, "_blank")}><FaShare /></ActionIcon>
                            </Tooltip>}
                        </Group>
                    </Paper>
                </UnstyledButton>
            ))
        }
    </Stack>

    const ShareStep = <Box>
        <Title ta="center" order={3} mt="md">Onboard Finished!</Title>
        <Title ta="center" order={5} c="gray" mt="md">Now all you need to do is share with your clients you platform. And keep in mind that anytime you need just go to Whitelabel Settings or Services menu at left side of Awaz dashboard and manage all your products and informations.</Title>
        <Group align="center" justify="center" mt="xl" mb="xl">
            <Tooltip label="Share on Facebook"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(userData?.company?.defaultDomain)}`, '_blank');
            }}><FaFacebook size={24} /></ActionIcon></Tooltip>
            <Tooltip label="Share on Whatsapp"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(userData?.company?.name)}%20-%20${encodeURIComponent(userData?.company?.defaultDomain)}`, '_blank');
            }}><FaWhatsapp size={24} /></ActionIcon></Tooltip>
            <Tooltip label="Share on Pinterest"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(userData?.company?.defaultDomain)}&media=${encodeURIComponent(userData?.company?.image)}&description=${encodeURIComponent(userData?.company?.name)}`, '_blank');
            }}><FaPinterest size={24} /></ActionIcon></Tooltip>
            <Tooltip label="Share on Reddit"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(userData?.company?.defaultDomain)}&title=${encodeURIComponent(userData?.company?.name)}`, '_blank');
            }}><FaReddit size={24} /></ActionIcon></Tooltip>
            <Tooltip label="Share on Twitter"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(userData?.company?.defaultDomain)}&text=${encodeURIComponent(userData?.company?.name)}`, '_blank');
            }}><FaTwitter size={24} /></ActionIcon></Tooltip>
            <Tooltip label="Share on Linkedin"><ActionIcon size="xl" variant="light" onClick={() => {
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(userData?.company?.defaultDomain)}`, '_blank');
            }}><FaLinkedin size={24} /></ActionIcon></Tooltip>
        </Group>
        <UnstyledButton style={{ width: '100%' }} onClick={() => {
            window.open(`http://${userData?.company?.defaultDomain}`)
        }}>
            <Text ta="center" c="dark" size="sm" mt="xs" style={{ textDecoration: 'underline' }}>{userData?.company?.defaultDomain}</Text>
        </UnstyledButton>
    </Box>;

    const SelectedIntegrationStep = selectedIntegration && <Stack>
        <ActionIcon
            onClick={() => setSelectedIntegration(undefined)}
        >
            <FaArrowLeft />
        </ActionIcon>
        <Paper shadow="xs" p="md" style={{ background: theme.colors.dark[9] }}>
            {selectedIntegration === "Twilio" && <Twilio connectOnly default />}
            {selectedIntegration === "OpenAi" && <OpenAi />}
            {selectedIntegration === "Retell" && <Retell />}
            {selectedIntegration === "Stripe" && <Stripe />}
            {selectedIntegration === "Sendgrid" && <Sendgrid />}
            {selectedIntegration === "Intercom" && <Intercom />}
        </Paper>
    </Stack>

    const continueForm = <>
        <Box mt="xl">
            <Button
                onClick={() => {
                    if (selectedTab + 1 === steps.length) {
                        window.location.href = `https://${data?.whitelabel?.label}.awaz.ai`
                    } else {
                        navigateToTab(selectedTab + 1, true)
                    }
                }}
                type="button"
                loading={loading}
                color="green"
                variant="filled"
                fullWidth
                size="lg">{selectedTab + 1 === steps.length ? "Go to Dashboard" : "Continue"}</Button>
        </Box>
        {/* <Box mt="md">
            {selectedTab + 1 < steps.length && <Button variant="transparent" fullWidth c="gray" onClick={() => {
                loadUserData();
                navigate("/");
            }}>Skip Onboarding</Button>}
        </Box> */}
    </>

    const activeForm = [
        { principal: UserStep, continueRight: true },
        { principal: CompanyStep, continueRight: true },
        //   { principal: AgentStep, secondary: PromptStep },
        //   { principal: ServicesStep, secondary: ServiceDetailsStep },
        { principal: WhitelabelStep },
        { principal: IntegrationsStep, secondary: SelectedIntegrationStep },
        //   { principal: PaymentStep, secondary: StripeStep },
        { principal: ShareStep },
    ].filter(nn => nn)[selectedTab]

    useEffect(() => {
        if (data?.general && !data?.general?.country) {
            axios.get("https://ipinfo.io")
                .then(({ data }) => {
                    handleChangeGeneral({ country: data.country });
                })
                .catch(err => { });
        }
    }, [data]);

    useEffect(() => {
        if (!userData?.wlPlan) {
            navigate("/");
        }
    }, [userData]);

    useEffect(() => {
        setLoading(true);
        GET_COMPANY_ONBOARDING_DATA()
            .then(res => {
                setValidLabel(true);
                setData(res);
                setLoading(false);
            })
            .catch(err => {
                notifications.show({ title: "Ops", message: err.message, color: 'red' })
                setLoading(false);
            })
    }, []);

    useEffect(() => {
        if (hash) {
            const tabIndex = steps.findIndex(s => s.tag === hash.split("#")[1])
            setSelectedTab(tabIndex > -1 ? tabIndex : 0);
        }
    }, [hash, steps]);

    if (!role) return null;
    return role?.profile?.title === "Client"
        ? <Navigate to="/" />
        : (
            <div style={{
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundAttachment: 'fixed',
                position: 'relative',
                minHeight: '100vh',
                zIndex: 0,
                ...(
                    isMobile
                        ? {
                            padding: 20,
                        }
                        : {
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: activeForm?.secondary ? '10vh 10vw' : '10vh 25vw',
                        }
                )
            }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0, background: '#00000055', zIndex: -1 }}></div>
                <Paper p="md" style={{ borderColor: '#DFDFDF' }}>
                    {isMobile && activeForm?.secondary
                        ? activeForm?.secondary
                        : <Grid>
                            <Grid.Col span={{ base: 12, md: 12 }}>
                                <div style={{ display: 'flex', margin: '40px 0', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                                    <img alt="awaz" src={companyHost?.image ? companyHost.image : `/logo-white.png`} style={{ maxHeight: 30 }} />
                                </div>
                                <Group style={{ padding: isMobile ? '0 4px' : '0 60px' }}>
                                    <ActionIcon onClick={() => setSelectedTab(i => i - 1)} style={{ visibility: selectedTab === 0 ? 'hidden' : 'visible' }}><FaChevronLeft /></ActionIcon>
                                    <Box style={{ flex: 1 }}>
                                        <Text ta="center" c="gray" size="sm">{steps[selectedTab]?.subtitle}</Text>
                                        <Title ta="center" order={2}>{steps[selectedTab]?.title}</Title>
                                    </Box>
                                    <ActionIcon style={{ visibility: 'hidden' }}><FaChevronLeft /></ActionIcon>
                                </Group>
                                <Box style={{ padding: isMobile ? '20px 10px 0 10px' : '40px 60px 0 60px' }}>
                                    <Box style={{ padding: '0 0 40px 0' }}>
                                        <Progress
                                            value={(((selectedTab ?? 0) + 1) / (steps.length)) * 100}
                                            color="dark"
                                        />
                                    </Box>
                                </Box>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: activeForm?.secondary ? 7 : 12 }}>
                                <Box style={{ padding: isMobile ? '0 10px 10px 10px' : '0 60px 60px 60px' }}>
                                    <form onSubmit={e => { e.preventDefault(); navigateToTab(selectedTab + 1, true) }} style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                                        {activeForm?.principal}
                                        {(!activeForm.continueRight || !activeForm.secondary) && continueForm}
                                    </form>
                                </Box>
                            </Grid.Col>
                            {activeForm?.secondary && !isMobile && <Grid.Col span={{ base: 12, md: 5 }}>
                                {activeForm.secondary}
                                {activeForm.continueRight && continueForm}
                            </Grid.Col>}
                        </Grid>}
                </Paper>
            </div>
        );
};
